import { DiagnosticEvent } from '~/common/models/alert.model';

export type VehicleHealthCopyKeys = {
  sectionTitle: string;
  sectionTitleSingular: string;
  count: string;
  allCount: string;
  accordionTitle: string;
  unsupported: string;
  requiredEvent: string;
  defaultCardTitle: string;
};

/**
 * Selects the notification copy keys for the "vehicle health" rename, gated by
 * BATTERY_12V_PROGNOSTICS_FLAG. Centralizing the branch keeps flag removal to one file.
 * @param enabled - Whether the flag is on.
 */
export const getVehicleHealthCopyKeys = (
  enabled: boolean
): VehicleHealthCopyKeys => ({
  sectionTitle: enabled
    ? 'settings:notifications.vehicles.vehicleHealthNotifications.title'
    : 'settings:notifications.vehicles.diagnosticsNotifications.title',
  sectionTitleSingular: enabled
    ? 'settings:notifications.vehicles.vehicleHealthNotifications.title_singular'
    : 'settings:notifications.vehicles.diagnosticsNotifications.title_singular',
  count: enabled
    ? 'settings:notifications.event'
    : 'settings:notifications.diagnostic',
  allCount: enabled
    ? 'settings:notifications.allEvents'
    : 'settings:notifications.allDiagnostics',
  accordionTitle: enabled
    ? 'settings:notification.card.diagnostics.eventsTitle'
    : 'asset:diagnostics',
  unsupported: enabled
    ? 'settings:notification.card.diagnostics.messages.unsupportedEvents'
    : 'settings:notification.card.diagnostics.messages.unsupported',
  requiredEvent: enabled
    ? 'settings:notification.card.form.requiredEventMessage'
    : 'settings:notification.card.form.requiredDiagnosticsMessage',
  defaultCardTitle: enabled
    ? 'settings:notification.card.name.vehicleHealthEvents'
    : 'common:diagnostics',
});

/**
 * Selects the heading key for the dashboard diagnostics widgets, which are
 * titled "Vehicle health" once the flag is on and "Diagnostics" before then.
 * @param enabled - Whether the flag is on.
 */
export const getDiagnosticsWidgetTitleKey = (enabled: boolean): string =>
  enabled
    ? 'dashboard:assetDiagnostics.title'
    : 'dashboard:assetDiagnostics.diagnosticsTitle';

/**
 * Selects the heading key for the vehicle details health card and its "Learn more" modal,
 * both titled "Vehicle health" once the flag is on and "Diagnostics" before then.
 * @param enabled - Whether the flag is on.
 */
export const getVehicleHealthTitleKey = (enabled: boolean): string =>
  enabled ? 'asset:vehicleHealth' : 'asset:diagnostics';

/**
 * Selects the label key for a diagnostic event checkbox. Only the low-battery
 * event is renamed ("12-volt battery") when the flag is on.
 * @param enabled - Whether the flag is on.
 */
export const getDiagnosticTypeKey = (
  event: DiagnosticEvent,
  enabled: boolean
): string =>
  enabled && event === DiagnosticEvent.LOW_BATTERY
    ? `settings:notification.card.diagnostics.type12Volt.${event}`
    : `settings:notification.card.diagnostics.type.${event}`;
